import React, { useState, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import AppLayout from 'layouts/AppLayout';
import Icon from 'components/AppIcon';
import Button from 'components/ui/Button';
import CategorySidebar from './components/CategorySidebar';
import DocumentCard from './components/DocumentCard';
import DocumentRow from './components/DocumentRow';
import DocumentPreviewModal from './components/DocumentPreviewModal';
import VersionHistoryDrawer from './components/VersionHistoryDrawer';
import AccessPermissionsModal from './components/AccessPermissionsModal';
import {
  DOCUMENTS,
  DOCUMENT_CATEGORIES,
  DR_FUNDS,
  DR_SECTORS,
  DR_PROJECTS,
} from './components/dataRoomData';
import ToastNotification from '../spv-dashboard/components/ToastNotification';
import { useRole } from 'context/RoleContext';
import { ROLES } from 'config/roleConfig';
import { ROUTES } from 'config/navConfig';

// ─── Helpers ──────────────────────────────────────────────────────────────────

function countDocsInScope(docs, { fundId, sectorId, projectId }) {
  return docs.filter(
    (d) =>
      (!fundId || d.fundId === fundId) &&
      (!sectorId || d.sectorId === sectorId) &&
      (!projectId || d.projectId === projectId)
  ).length;
}

// ─── Overview Tile Components ─────────────────────────────────────────────────

function FundTile({ fund, docCount, sectorCount, onSelect }) {
  const statusColor = fund.status === 'Closed'
    ? { text: 'text-green-700', bg: 'bg-green-100' }
    : { text: 'text-amber-700', bg: 'bg-amber-100' };

  return (
    <button
      onClick={() => onSelect({ fundId: fund.id, sectorId: null, projectId: null })}
      className="bg-card border border-border rounded-2xl p-5 text-left hover:border-primary/40 hover:shadow-md transition-all duration-200 group"
    >
      <div className="flex items-start justify-between mb-3">
        <div
          className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
          style={{ background: 'var(--color-primary)', opacity: 0.12 }}
        />
        <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 absolute"
          style={{ background: 'rgba(59,76,76,0.1)' }}>
          <Icon name={fund.icon} size={20} color="var(--color-primary)" />
        </div>
        <span className={`font-caption text-xs font-semibold px-2 py-0.5 rounded-full ${statusColor.bg} ${statusColor.text}`}>
          {fund.status}
        </span>
      </div>
      <div className="mt-1">
        <p className="font-heading text-sm font-bold text-foreground group-hover:text-primary transition-colors">
          {fund.label}
        </p>
        <p className="font-caption text-xs text-muted-foreground mt-0.5">{fund.description}</p>
      </div>
      <div className="flex items-center gap-4 mt-4 pt-3 border-t border-border">
        <div className="flex items-center gap-1.5">
          <Icon name="FolderOpen" size={13} color="var(--color-muted-foreground)" />
          <span className="font-caption text-xs text-muted-foreground">{docCount} docs</span>
        </div>
        <div className="flex items-center gap-1.5">
          <Icon name="Layers" size={13} color="var(--color-muted-foreground)" />
          <span className="font-caption text-xs text-muted-foreground">{sectorCount} sectors</span>
        </div>
        <div className="ml-auto">
          <Icon name="ChevronRight" size={14} color="var(--color-primary)" className="opacity-0 group-hover:opacity-100 transition-opacity" />
        </div>
      </div>
    </button>
  );
}

function SectorTile({ sector, docCount, projectCount, onSelect }) {
  return (
    <button
      onClick={() => onSelect({ fundId: sector.fundId, sectorId: sector.id, projectId: null })}
      className="bg-card border border-border rounded-2xl p-5 text-left hover:border-primary/40 hover:shadow-md transition-all duration-200 group"
    >
      <div className="flex items-center gap-3 mb-3">
        <div className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0"
          style={{ background: 'rgba(59,76,76,0.1)' }}>
          <Icon name={sector.icon} size={18} color="var(--color-primary)" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="font-heading text-sm font-bold text-foreground group-hover:text-primary transition-colors truncate">
            {sector.label}
          </p>
          <p className="font-caption text-xs text-muted-foreground">{projectCount} projects</p>
        </div>
        <Icon name="ChevronRight" size={14} color="var(--color-primary)" className="opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0" />
      </div>
      <div className="flex items-center gap-1.5 mt-1">
        <Icon name="FolderOpen" size={13} color="var(--color-muted-foreground)" />
        <span className="font-caption text-xs text-muted-foreground">{docCount} documents</span>
      </div>
    </button>
  );
}

function ProjectTile({ project, allDocs, onSelect }) {
  const projDocs = allDocs.filter((d) => d.projectId === project.id);
  const docCount = projDocs.length;

  // Category breakdown chips
  const catCounts = DOCUMENT_CATEGORIES.slice(1).reduce((acc, cat) => {
    const n = projDocs.filter((d) => d.category === cat.id).length;
    if (n > 0) acc.push({ label: cat.label, count: n });
    return acc;
  }, []);

  return (
    <button
      onClick={() =>
        onSelect({ fundId: project.fundId, sectorId: project.sectorId, projectId: project.id })
      }
      className="bg-card border border-border rounded-2xl p-5 text-left hover:border-primary/40 hover:shadow-md transition-all duration-200 group"
    >
      <div className="flex items-start justify-between mb-3">
        <div className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0"
          style={{ background: 'rgba(59,76,76,0.1)' }}>
          <Icon name={project.icon} size={18} color="var(--color-primary)" />
        </div>
        <span className="font-caption text-xs text-muted-foreground">{project.country}</span>
      </div>
      <p className="font-heading text-sm font-bold text-foreground group-hover:text-primary transition-colors mb-1">
        {project.label}
      </p>
      <p className="font-caption text-xs text-muted-foreground mb-3">
        {docCount} document{docCount !== 1 ? 's' : ''}
      </p>
      {catCounts.length > 0 && (
        <div className="flex flex-wrap gap-1">
          {catCounts.map((c) => (
            <span
              key={c.label}
              className="font-caption text-xs px-2 py-0.5 rounded-full bg-muted text-muted-foreground"
            >
              {c.label} ({c.count})
            </span>
          ))}
        </div>
      )}
    </button>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────

export default function DataRoom() {
  const navigate = useNavigate();
  const { role, isExternal } = useRole();

  const userRole =
    role === ROLES.PLATFORM_ADMIN
      ? 'admin'
      : role === ROLES.INVESTMENT_TEAM ||
        role === ROLES.LEGAL_COMP_TEAM ||
        role === ROLES.FINANCE_TEAM ||
        role === ROLES.ASSET_MGMT_TEAM ||
        role === ROLES.IC_MEMBER
      ? 'sponsor'
      : 'investor';

  // ── Navigation state ──────────────────────────────────────────────────────
  const [selection, setSelection] = useState({ fundId: null, sectorId: null, projectId: null });
  const [docCategory, setDocCategory] = useState('all');

  const handleSelect = useCallback((newSelection) => {
    setSelection(newSelection);
    setDocCategory('all');
  }, []);

  // ── Filter / sort state ───────────────────────────────────────────────────
  const [searchQuery, setSearchQuery] = useState('');
  const [viewMode, setViewMode] = useState('grid');
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [fileTypeFilter, setFileTypeFilter] = useState('all');
  const [accessLevelFilter, setAccessLevelFilter] = useState('all');
  const [dateModifiedFilter, setDateModifiedFilter] = useState('all');
  const [sortField, setSortField] = useState(null);
  const [sortDir, setSortDir] = useState('asc');

  // ── Modal/Drawer state ────────────────────────────────────────────────────
  const [previewDoc, setPreviewDoc] = useState(null);
  const [historyDoc, setHistoryDoc] = useState(null);
  const [accessDoc, setAccessDoc] = useState(null);

  // ── Toast ─────────────────────────────────────────────────────────────────
  const [toasts, setToasts] = useState([]);
  const addToast = useCallback((toast) => {
    const id = Date.now().toString();
    setToasts((prev) => [...prev, { ...toast, id }]);
  }, []);
  const dismissToast = useCallback(
    (id) => setToasts((prev) => prev.filter((t) => t.id !== id)),
    []
  );

  const [documents, setDocuments] = useState(DOCUMENTS);

  const handleSort = (field) => {
    if (sortField === field) setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    else { setSortField(field); setSortDir('asc'); }
  };

  const getDateThreshold = (range) => {
    const now = new Date();
    if (range === '7d')  return new Date(now - 7 * 86400000);
    if (range === '30d') return new Date(now - 30 * 86400000);
    if (range === '3m')  return new Date(now.setMonth(now.getMonth() - 3));
    if (range === '1y')  return new Date(now.setFullYear(now.getFullYear() - 1));
    return null;
  };

  // ── Derived: docs in current scope (fund/sector/project) ─────────────────
  const scopedDocs = useMemo(() => {
    let docs = documents;
    if (selection.fundId)    docs = docs.filter((d) => d.fundId === selection.fundId);
    if (selection.sectorId)  docs = docs.filter((d) => d.sectorId === selection.sectorId);
    if (selection.projectId) docs = docs.filter((d) => d.projectId === selection.projectId);
    return docs;
  }, [documents, selection]);

  // ── Derived: filtered + sorted docs for the document list ────────────────
  const filteredDocs = useMemo(() => {
    let docs = scopedDocs;
    if (docCategory !== 'all') docs = docs.filter((d) => d.category === docCategory);
    if (searchQuery?.trim()) {
      const q = searchQuery.toLowerCase();
      docs = docs.filter(
        (d) =>
          d?.name?.toLowerCase().includes(q) ||
          d?.category?.toLowerCase().includes(q) ||
          d?.modifiedBy?.toLowerCase().includes(q)
      );
    }
    if (fileTypeFilter !== 'all')   docs = docs.filter((d) => d.fileType === fileTypeFilter);
    if (accessLevelFilter !== 'all') docs = docs.filter((d) => d.accessLevel === accessLevelFilter);
    if (dateModifiedFilter !== 'all') {
      const threshold = getDateThreshold(dateModifiedFilter);
      if (threshold) {
        docs = docs.filter((d) => {
          const parts = d?.modifiedDate?.split(' ');
          if (parts?.length < 3) return true;
          const months = { Jan:0,Feb:1,Mar:2,Apr:3,May:4,Jun:5,Jul:6,Aug:7,Sep:8,Oct:9,Nov:10,Dec:11 };
          const docDate = new Date(parseInt(parts[2]), months[parts[1]], parseInt(parts[0]));
          return docDate >= threshold;
        });
      }
    }
    if (sortField) {
      docs = [...docs].sort((a, b) => {
        let av = a?.[sortField];
        let bv = b?.[sortField];
        if (typeof av === 'string') av = av.toLowerCase();
        if (typeof bv === 'string') bv = bv.toLowerCase();
        if (av < bv) return sortDir === 'asc' ? -1 : 1;
        if (av > bv) return sortDir === 'asc' ? 1 : -1;
        return 0;
      });
    }
    return docs;
  }, [scopedDocs, docCategory, searchQuery, fileTypeFilter, accessLevelFilter, dateModifiedFilter, sortField, sortDir]);

  // ── Category tabs — only show categories that exist in current project ────
  const availableCategories = useMemo(() => {
    if (!selection.projectId) return [];
    return DOCUMENT_CATEGORIES.filter(
      (cat) => cat.id === 'all' || scopedDocs.some((d) => d.category === cat.id)
    ).map((cat) => ({
      ...cat,
      count: cat.id === 'all' ? scopedDocs.length : scopedDocs.filter((d) => d.category === cat.id).length,
    }));
  }, [scopedDocs, selection.projectId]);

  // ── Breadcrumb segments ───────────────────────────────────────────────────
  const breadcrumb = useMemo(() => {
    const crumbs = [{ label: 'Document Library', onClick: () => handleSelect({ fundId: null, sectorId: null, projectId: null }) }];
    if (selection.fundId) {
      const fund = DR_FUNDS.find((f) => f.id === selection.fundId);
      crumbs.push({ label: fund?.shortLabel, onClick: () => handleSelect({ fundId: selection.fundId, sectorId: null, projectId: null }) });
    }
    if (selection.sectorId) {
      const sector = DR_SECTORS.find((s) => s.id === selection.sectorId);
      crumbs.push({ label: sector?.label, onClick: () => handleSelect({ fundId: selection.fundId, sectorId: selection.sectorId, projectId: null }) });
    }
    if (selection.projectId) {
      const proj = DR_PROJECTS.find((p) => p.id === selection.projectId);
      crumbs.push({ label: proj?.label, onClick: null });
    }
    return crumbs;
  }, [selection]);

  // ── Handlers ──────────────────────────────────────────────────────────────
  const handleDownload = (doc) =>
    addToast({ type: 'success', title: 'Download started', message: `${doc?.name} is downloading…` });

  const handleDelete = (doc) => {
    setDocuments((prev) => prev.filter((d) => d.id !== doc.id));
    addToast({ type: 'info', title: 'Document deleted', message: `${doc?.name} has been removed.` });
  };

  const handleUpload = () =>
    addToast({ type: 'success', title: 'Upload successful', message: 'Document uploaded to the Document Library.' });

  const handleViewHistory = (doc) => { setHistoryDoc(doc); if (previewDoc) setPreviewDoc(null); };
  const handleManageAccess = (doc) => { setAccessDoc(doc); if (previewDoc) setPreviewDoc(null); };

  const canUpload = !isExternal && (
    role === ROLES.PLATFORM_ADMIN ||
    role === ROLES.INVESTMENT_TEAM ||
    role === ROLES.LEGAL_COMP_TEAM ||
    role === ROLES.FINANCE_TEAM ||
    role === ROLES.ASSET_MGMT_TEAM
  );

  // ── Overview data ─────────────────────────────────────────────────────────
  const overviewFunds = DR_FUNDS.map((f) => ({
    ...f,
    docCount: countDocsInScope(documents, { fundId: f.id }),
    sectorCount: DR_SECTORS.filter((s) => s.fundId === f.id).length,
  }));

  const overviewSectors = selection.fundId
    ? DR_SECTORS.filter((s) => s.fundId === selection.fundId).map((s) => ({
        ...s,
        docCount: countDocsInScope(documents, { sectorId: s.id }),
        projectCount: DR_PROJECTS.filter((p) => p.sectorId === s.id).length,
      }))
    : [];

  const overviewProjects = selection.sectorId
    ? DR_PROJECTS.filter((p) => p.sectorId === selection.sectorId)
    : [];

  // Determine what level the main content area is showing
  const viewLevel = selection.projectId ? 'documents' : selection.sectorId ? 'projects' : selection.fundId ? 'sectors' : 'funds';

  return (
    <AppLayout>
      <div className="flex flex-col h-screen overflow-hidden">
        {/* Page Header */}
        <header className="flex-shrink-0 bg-background border-b border-border px-6 py-4">
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div>
              <h1 className="font-heading text-xl font-bold text-foreground">Document Library</h1>
              <p className="text-xs font-caption text-muted-foreground mt-0.5">
                Secure document repository — Sample data, for demonstration purposes only
              </p>
            </div>
            <div className="flex items-center gap-3 flex-wrap">
              {/* Search — only relevant when viewing documents */}
              {viewLevel === 'documents' && (
                <div className="relative">
                  <Icon name="Search" size={15} className="absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" color="var(--color-muted-foreground)" />
                  <input
                    type="text"
                    placeholder="Search documents…"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-9 pr-4 py-2 rounded-lg border border-border bg-background text-sm font-caption text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 transition-all duration-200 w-56"
                  />
                </div>
              )}
              {viewLevel === 'documents' && (
                <>
                  <select value={fileTypeFilter} onChange={(e) => setFileTypeFilter(e.target.value)}
                    className="px-3 py-2 rounded-lg border border-border bg-background text-sm font-caption text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 transition-all">
                    <option value="all">All Types</option>
                    <option value="pdf">PDF</option>
                    <option value="xlsx">Excel</option>
                    <option value="docx">Word</option>
                  </select>
                  <select value={accessLevelFilter} onChange={(e) => setAccessLevelFilter(e.target.value)}
                    className="px-3 py-2 rounded-lg border border-border bg-background text-sm font-caption text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 transition-all">
                    <option value="all">All Levels</option>
                    <option value="restricted">Restricted</option>
                    <option value="confidential">Confidential</option>
                    <option value="internal">Internal</option>
                    <option value="public">Public</option>
                  </select>
                  <select value={dateModifiedFilter} onChange={(e) => setDateModifiedFilter(e.target.value)}
                    className="px-3 py-2 rounded-lg border border-border bg-background text-sm font-caption text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 transition-all">
                    <option value="all">Any Time</option>
                    <option value="7d">Last 7 Days</option>
                    <option value="30d">Last 30 Days</option>
                    <option value="3m">Last 3 Months</option>
                    <option value="1y">Last Year</option>
                  </select>
                </>
              )}
              {canUpload && (
                <button
                  onClick={handleUpload}
                  className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-caption font-semibold bg-primary text-white hover:bg-primary/90 transition-all duration-200"
                >
                  <Icon name="Upload" size={15} />
                  Upload
                </button>
              )}
              {viewLevel === 'documents' && (
                <div className="flex items-center border border-border rounded-lg overflow-hidden">
                  <button onClick={() => setViewMode('grid')}
                    className={`flex items-center justify-center w-9 h-9 transition-all duration-200 ${viewMode === 'grid' ? 'bg-primary text-white' : 'text-muted-foreground hover:bg-muted'}`}
                    title="Grid view">
                    <Icon name="LayoutGrid" size={15} />
                  </button>
                  <button onClick={() => setViewMode('list')}
                    className={`flex items-center justify-center w-9 h-9 transition-all duration-200 ${viewMode === 'list' ? 'bg-primary text-white' : 'text-muted-foreground hover:bg-muted'}`}
                    title="List view">
                    <Icon name="List" size={15} />
                  </button>
                </div>
              )}
            </div>
          </div>
        </header>

        {/* Role-aware banner */}
        {isExternal ? (
          <div className="flex-shrink-0 flex items-start gap-3 px-6 py-3"
            style={{ background: 'rgba(217,119,6,0.07)', borderBottom: '1px solid rgba(217,119,6,0.2)' }}>
            <Icon name="AlertTriangle" size={16} color="#b45309" style={{ flexShrink: 0, marginTop: 2 }} />
            <div className="flex-1 min-w-0">
              <p className="font-caption text-sm font-semibold" style={{ color: '#b45309' }}>Internal Document Library</p>
              <p className="font-caption text-xs text-muted-foreground mt-0.5">
                This is a restricted internal repository. Your deal-specific documents are available in the Deal Room.
              </p>
            </div>
            <Button variant="outline" size="sm" iconName="ExternalLink" iconPosition="right" iconSize={13} onClick={() => navigate(ROUTES.dealRoom)}>
              Go to Deal Room
            </Button>
          </div>
        ) : (
          <div className="flex-shrink-0 flex items-center gap-2 px-6 py-2"
            style={{ background: 'rgba(59,76,76,0.04)', borderBottom: '1px solid rgba(59,76,76,0.08)' }}>
            <Icon name="Shield" size={13} color="var(--color-primary)" />
            <p className="font-caption text-xs text-muted-foreground">
              Internal only · Confidential · Do not distribute
            </p>
          </div>
        )}

        {/* Body: Sidebar + Main */}
        <div className="flex flex-1 overflow-hidden">
          <CategorySidebar
            selection={selection}
            onSelect={handleSelect}
            isCollapsed={sidebarCollapsed}
            onToggleCollapse={() => setSidebarCollapsed((p) => !p)}
          />

          <main className="flex-1 overflow-y-auto bg-background">
            {/* Breadcrumb */}
            <div className="flex items-center gap-1.5 px-6 py-3 border-b border-border bg-card flex-wrap">
              {breadcrumb.map((crumb, i) => (
                <React.Fragment key={i}>
                  {i > 0 && <Icon name="ChevronRight" size={12} color="var(--color-muted-foreground)" />}
                  {crumb.onClick ? (
                    <button
                      onClick={crumb.onClick}
                      className="font-caption text-xs text-muted-foreground hover:text-primary transition-colors"
                    >
                      {crumb.label}
                    </button>
                  ) : (
                    <span className="font-caption text-xs text-foreground font-medium">{crumb.label}</span>
                  )}
                </React.Fragment>
              ))}
              {viewLevel === 'documents' && (
                <span className="ml-auto font-caption text-xs text-muted-foreground">
                  {filteredDocs.length} document{filteredDocs.length !== 1 ? 's' : ''}
                </span>
              )}
            </div>

            {/* Doc category tabs — shown only when a project is selected */}
            {viewLevel === 'documents' && availableCategories.length > 0 && (
              <div className="flex items-center gap-1 px-6 py-3 border-b border-border bg-card overflow-x-auto">
                {availableCategories.map((cat) => (
                  <button
                    key={cat.id}
                    onClick={() => setDocCategory(cat.id)}
                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg font-caption text-xs font-medium whitespace-nowrap transition-all duration-150 ${
                      docCategory === cat.id
                        ? 'bg-primary text-white'
                        : 'bg-muted text-muted-foreground hover:text-foreground hover:bg-muted/80'
                    }`}
                  >
                    <Icon name={cat.icon} size={12} color="currentColor" />
                    {cat.label}
                    <span className={`px-1.5 py-0.5 rounded-full text-xs font-semibold ${
                      docCategory === cat.id ? 'bg-white/20 text-white' : 'bg-background text-muted-foreground'
                    }`}>
                      {cat.count}
                    </span>
                  </button>
                ))}
              </div>
            )}

            <div className="p-6">
              {/* ── Fund overview ── */}
              {viewLevel === 'funds' && (
                <div>
                  <p className="font-caption text-sm text-muted-foreground mb-4">
                    Select a fund to browse its documents by sector and project.
                  </p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                    {overviewFunds.map((fund) => (
                      <div key={fund.id} className="relative">
                        <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 absolute top-5 left-5 z-10"
                          style={{ background: 'rgba(59,76,76,0.1)' }}>
                          <Icon name={fund.icon} size={20} color="var(--color-primary)" />
                        </div>
                        <FundTile fund={fund} docCount={fund.docCount} sectorCount={fund.sectorCount} onSelect={handleSelect} />
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* ── Sector overview ── */}
              {viewLevel === 'sectors' && (
                <div>
                  <p className="font-caption text-sm text-muted-foreground mb-4">
                    {DR_FUNDS.find((f) => f.id === selection.fundId)?.label} — select a sector to view its projects.
                  </p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                    {overviewSectors.map((sector) => (
                      <SectorTile key={sector.id} sector={sector} docCount={sector.docCount} projectCount={sector.projectCount} onSelect={handleSelect} />
                    ))}
                  </div>
                </div>
              )}

              {/* ── Project overview ── */}
              {viewLevel === 'projects' && (
                <div>
                  <p className="font-caption text-sm text-muted-foreground mb-4">
                    {DR_SECTORS.find((s) => s.id === selection.sectorId)?.label} — select a project to view its documents.
                  </p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                    {overviewProjects.map((proj) => (
                      <ProjectTile key={proj.id} project={proj} allDocs={documents} onSelect={handleSelect} />
                    ))}
                  </div>
                </div>
              )}

              {/* ── Document list ── */}
              {viewLevel === 'documents' && (
                <>
                  {filteredDocs.length === 0 ? (
                    <div className="flex flex-col items-center justify-center py-20 gap-4">
                      <div className="flex items-center justify-center w-16 h-16 rounded-2xl bg-muted">
                        <Icon name="FolderOpen" size={28} color="var(--color-muted-foreground)" />
                      </div>
                      <div className="text-center">
                        <p className="font-heading text-base font-semibold text-foreground mb-1">No documents found</p>
                        <p className="text-sm font-caption text-muted-foreground">
                          {searchQuery ? 'Try adjusting your search query.' : 'No documents in this category.'}
                        </p>
                      </div>
                      {searchQuery && (
                        <button onClick={() => setSearchQuery('')} className="text-sm font-caption text-primary hover:underline">
                          Clear search
                        </button>
                      )}
                    </div>
                  ) : viewMode === 'grid' ? (
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                      {filteredDocs.map((doc) => (
                        <DocumentCard key={doc.id} doc={doc} userRole={userRole}
                          onPreview={setPreviewDoc} onDownload={handleDownload}
                          onViewHistory={handleViewHistory} onManageAccess={handleManageAccess}
                          onDelete={handleDelete} />
                      ))}
                    </div>
                  ) : (
                    <div className="bg-card rounded-xl border border-border overflow-hidden">
                      <table className="w-full">
                        <thead>
                          <tr className="border-b border-border bg-muted/50">
                            {[
                              { field: 'name', label: 'Document' },
                              { field: 'category', label: 'Category' },
                              { field: 'version', label: 'Version' },
                              { field: 'modifiedDate', label: 'Modified' },
                              { field: 'fileSize', label: 'Size' },
                              { field: 'accessLevel', label: 'Access' },
                            ].map(({ field, label }) => (
                              <th key={field} onClick={() => handleSort(field)}
                                className="px-4 py-3 text-left text-xs font-caption font-semibold uppercase tracking-wider cursor-pointer select-none hover:bg-muted/80 transition-colors"
                                style={{ color: sortField === field ? 'var(--color-primary)' : 'var(--color-muted-foreground)' }}>
                                <span className="flex items-center gap-1">
                                  {label}
                                  {sortField === field ? (
                                    <Icon name={sortDir === 'asc' ? 'ChevronUp' : 'ChevronDown'} size={12} color="var(--color-primary)" />
                                  ) : (
                                    <Icon name="ChevronsUpDown" size={12} color="var(--color-muted-foreground)" />
                                  )}
                                </span>
                              </th>
                            ))}
                            <th className="px-4 py-3 text-left text-xs font-caption font-semibold text-muted-foreground uppercase tracking-wider">Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {filteredDocs.map((doc) => (
                            <DocumentRow key={doc.id} doc={doc} userRole={userRole}
                              onPreview={setPreviewDoc} onDownload={handleDownload}
                              onViewHistory={handleViewHistory} onManageAccess={handleManageAccess}
                              onDelete={handleDelete} />
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </>
              )}
            </div>
          </main>
        </div>
      </div>

      {previewDoc && (
        <DocumentPreviewModal doc={previewDoc} userRole={userRole} onClose={() => setPreviewDoc(null)}
          onDownload={handleDownload} onViewHistory={handleViewHistory}
          onManageAccess={handleManageAccess} onToast={addToast} />
      )}
      {historyDoc && (
        <VersionHistoryDrawer doc={historyDoc} onClose={() => setHistoryDoc(null)} onToast={addToast} />
      )}
      {accessDoc && (
        <AccessPermissionsModal doc={accessDoc} userRole={userRole} onClose={() => setAccessDoc(null)} onToast={addToast} />
      )}
      <ToastNotification toasts={toasts} onDismiss={dismissToast} />
    </AppLayout>
  );
}
